import { useState } from 'react';
import { ArrowUpOnSquareIcon } from '@heroicons/react/24/outline';
import { Links } from '../config/links';

interface Props {
  isLanding: boolean;
  onAnalyze?: () => void;
  showShare?: boolean;
  onShare?: () => void;
  showCenterTitle?: boolean;
}

const Header = ({ isLanding, onAnalyze, showShare, onShare, showCenterTitle }: Props) => {
  const [open, setOpen] = useState(false);

  const NavLinks = (
    <div className="flex items-center gap-5">
      {isLanding && (
        <>
          <button
            type="button"
            className="text-sm text-slate-700 hover:text-slate-900"
            onClick={() => {
              try {
                const el = document.getElementById('how-it-works');
                el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              } catch {}
            }}
          >
            How it Works
          </button>
          <a
            href={Links.ContactUs}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-slate-700 hover:text-slate-900"
          >
            Contact Us
          </a>
          <button type="button" onClick={onAnalyze} className="btn-primary btn-compact">
            Analyze Menu
          </button>
        </>
      )}
    </div>
  );

  return (
    <header className="w-full bg-transparent">
      <div className="relative mx-auto flex max-w-6xl items-end justify-between px-4 py-3">
        {/* Centered heading for non-landing, non-report pages */}
        {showCenterTitle ? (
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none">
            <span className="font-semibold text-slate-700 text-sm sm:text-base md:text-lg">Menu Analysis Tool</span>
          </div>
        ) : null}
        {/* Brand: desktop shows full logo + Labs; mobile shows square logo only */}
        <div className="hidden md:flex items-end gap-2">
          <a href={Links.MainSite} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2">
            <img
              src="/fastbar-logo-forwhitebg.svg"
              alt="FastBar"
              className="h-8 w-auto select-none"
            />
          </a>
          <span className="rounded-md bg-slate-100/70 px-2 py-0.5 text-xs font-semibold uppercase tracking-widest text-slate-800 mb-[1px]">
            Labs
          </span>
        </div>
        <div className="flex md:hidden items-center">
          <a href={Links.MainSite} target="_blank" rel="noopener noreferrer" className="flex items-center">
            <img
              src="/fastbar-logo-square-forwhite.svg"
              alt="FastBar"
              className="h-8 w-8 select-none"
            />
          </a>
        </div>

        {/* Desktop nav */}
        <nav className="hidden items-center gap-5 md:flex">
          {isLanding ? (
            NavLinks
          ) : showShare ? (
            <button type="button" onClick={onShare} className="btn-primary btn-compact">
              <ArrowUpOnSquareIcon className="h-4 w-4" />
              Share
            </button>
          ) : null}
        </nav>

        {/* Mobile menu */}
        <div className="md:hidden">
          {isLanding ? (
            <button
              type="button"
              aria-label="Open menu"
              className="rounded-md border border-slate-300 bg-white p-2 text-slate-700 shadow-sm"
              onClick={() => setOpen((v) => !v)}
            >
              {/* Simple hamburger */}
              <span className="block h-0.5 w-5 bg-current" />
              <span className="mt-1 block h-0.5 w-5 bg-current" />
              <span className="mt-1 block h-0.5 w-5 bg-current" />
            </button>
          ) : (
            showShare ? (
              <button type="button" onClick={onShare} className="btn-primary btn-compact inline-flex items-center gap-2">
                <ArrowUpOnSquareIcon className="h-4 w-4" />
                Share
              </button>
            ) : null
          )}
        </div>
      </div>

      {isLanding && open && (
        <div className="md:hidden bg-transparent">
          <div className="mx-auto max-w-6xl px-4 py-3 space-y-3">
            <button
              type="button"
              className="block w-full text-left text-sm text-slate-800"
              onClick={() => {
                setOpen(false);
                // Placeholder for later wiring
              }}
            >
              How it works
            </button>
            <a
              href={Links.ContactUs}
              target="_blank"
              rel="noopener noreferrer"
              className="block text-sm text-slate-800"
              onClick={() => setOpen(false)}
            >
              Contact Us
            </a>
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                onAnalyze?.();
              }}
              className="mt-2 inline-flex w-full items-center justify-center btn-primary btn-compact"
            >
              Analyze Menu
            </button>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
